/**
 * Score over time, one small multiple per subject.
 *
 * Small multiples rather than a single multi-series chart on purpose: a
 * categorical palette would need colors this design system does not have, and
 * with one line per panel the panel title names the series, so no legend and no
 * color-coding is needed at all. The y axis is fixed at 0–100% so the panels
 * are comparable side by side, and it is a percentage of the paper rather than
 * a raw mark because papers differ in total marks — 40 out of 50 and 40 out of
 * 80 are not the same result and must not sit at the same height.
 *
 * Retakes are the reason this chart is not just a line. An exam can be sat more
 * than once, and two points for the same paper are a resit, not two pieces of
 * new ground: joining them with the same solid line as everything else would
 * claim progress the student did not make. So the step into a resit is drawn
 * dashed, the resit marker is a square rather than a circle, and the caption
 * under the chart names the paper behind every position. Shape and text carry
 * that meaning, never colour.
 */
import { useState } from "react";
import { formatDate, formatMarks, formatPercent } from "../lib/format";
import type { ScorePointFact, ScoreSeries } from "./AnalyticsData";

const WIDTH = 320;
const HEIGHT = 132;
const PAD = { top: 12, right: 14, bottom: 22, left: 32 };

/** A resit gets a square; a first sitting gets a circle. */
function Marker({
  point,
  cx,
  cy,
  active,
}: {
  point: ScorePointFact;
  cx: number;
  cy: number;
  active: boolean;
}) {
  const r = active ? 5 : 4;
  const shared = {
    fill: "var(--color-surface)",
    stroke: "var(--color-ink)",
    strokeWidth: 2,
  };
  return point.attempt_no > 1 ? (
    <rect x={cx - r} y={cy - r} width={r * 2} height={r * 2} {...shared} />
  ) : (
    <circle cx={cx} cy={cy} r={r} {...shared} />
  );
}

export function ScoreTrend({ series }: { series: ScoreSeries }) {
  const [hover, setHover] = useState<number | null>(null);

  const points = series.points;
  const plotW = WIDTH - PAD.left - PAD.right;
  const plotH = HEIGHT - PAD.top - PAD.bottom;

  // A single attempt still deserves a dot; put it in the middle.
  const x = (i: number) =>
    PAD.left + (points.length <= 1 ? plotW / 2 : (i / (points.length - 1)) * plotW);
  const y = (ratio: number) => PAD.top + (1 - Math.max(0, Math.min(1, ratio))) * plotH;

  const active = hover === null ? null : points[hover];
  const retakes = points.filter((p) => p.attempt_no > 1).length;

  if (points.length === 0) {
    return (
      <div className="min-w-0">
        <p className="truncate text-xs font-medium text-ink">{series.subject_name}</p>
        <p className="mt-1 text-2xs text-ink-faint">
          Nothing graded in this subject yet — a chart appears once an attempt is
          marked.
        </p>
      </div>
    );
  }

  return (
    <div className="min-w-0">
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <p className="truncate text-xs font-medium text-ink">{series.subject_name}</p>
        <p className="tabular text-2xs text-ink-faint">
          {points.length} {points.length === 1 ? "sitting" : "sittings"}
          {retakes > 0 ? `, ${retakes} a resit` : ""}
        </p>
      </div>

      <div className="relative">
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          className="w-full"
          role="img"
          aria-label={`${series.subject_name}. Score as a percentage of the paper, oldest sitting first, axis 0 to 100 percent. ${points
            .map(
              (p, i) =>
                `Position ${i + 1}: ${p.exam_title}${
                  p.attempt_no > 1 ? ` resit number ${p.attempt_no}` : ""
                }, ${formatPercent(p.ratio)}.`,
            )
            .join(" ")}`}
          onMouseLeave={() => setHover(null)}
        >
          {/* Recessive gridlines at 0 / 50 / 100%, with the unit on the axis. */}
          {[0, 0.5, 1].map((tick) => (
            <g key={tick}>
              <line
                x1={PAD.left}
                x2={WIDTH - PAD.right}
                y1={y(tick)}
                y2={y(tick)}
                stroke="var(--color-border)"
                strokeWidth={1}
              />
              <text
                x={PAD.left - 5}
                y={y(tick) + 3}
                textAnchor="end"
                fontSize={9}
                fill="var(--color-ink-faint)"
              >
                {tick * 100}%
              </text>
            </g>
          ))}

          {/* One segment at a time: a step into a resit of the same paper is
              dashed, so the eye does not read it as ground newly covered. */}
          {points.slice(1).map((point, idx) => {
            const from = points[idx]!;
            return (
              <line
                key={`seg-${point.attempt_id}`}
                x1={x(idx)}
                y1={y(from.ratio)}
                x2={x(idx + 1)}
                y2={y(point.ratio)}
                stroke="var(--color-ink-muted)"
                strokeWidth={2}
                strokeLinecap="round"
                strokeDasharray={point.follows_same_exam ? "3 3" : undefined}
              />
            );
          })}

          {points.map((point, i) => (
            <g key={point.attempt_id}>
              <title>
                {`${i + 1}. ${point.exam_title}${
                  point.attempt_no > 1 ? ` (resit #${point.attempt_no})` : ""
                } — ${formatPercent(point.ratio)}`}
              </title>
              {/* A generous invisible target — the visible marker is only 8px. */}
              <rect
                x={x(i) - 12}
                y={PAD.top}
                width={24}
                height={plotH}
                fill="transparent"
                onMouseEnter={() => setHover(i)}
              />
              <Marker point={point} cx={x(i)} cy={y(point.ratio)} active={hover === i} />
              <text
                x={x(i)}
                y={HEIGHT - 6}
                textAnchor="middle"
                fontSize={9}
                fill="var(--color-ink-faint)"
              >
                {i + 1}
              </text>
            </g>
          ))}

          {/* Direct label on the latest point only — never one per point. */}
          <text
            x={x(points.length - 1)}
            y={y(points[points.length - 1]!.ratio) - 8}
            textAnchor="end"
            fontSize={10}
            fill="var(--color-ink)"
            fontWeight={500}
          >
            {formatPercent(points[points.length - 1]!.ratio)}
          </text>
        </svg>

        {active ? (
          <div className="pointer-events-none absolute left-1/2 top-0 w-52 -translate-x-1/2 rounded-md border border-border bg-surface px-2.5 py-1.5 shadow-overlay">
            <p className="truncate text-2xs font-medium text-ink">{active.exam_title}</p>
            <p className="tabular text-2xs text-ink-muted">
              {formatMarks(active.score)} / {formatMarks(active.max_score)} ·{" "}
              {formatPercent(active.ratio)}
            </p>
            <p className="tabular text-2xs text-ink-faint">
              Sitting {active.attempt_no}
              {active.attempt_no > 1 ? " (resit)" : ""} ·{" "}
              <time>{formatDate(active.submitted_at)}</time>
            </p>
          </div>
        ) : null}
      </div>

      <p className="mt-0.5 text-2xs text-ink-faint">
        y: score, % of the paper (0–100). x: sittings, oldest first.
      </p>

      {/* Every position is named. Without this the x axis is bare integers and
          "which paper was position 3" is unanswerable. */}
      <ol className="mt-1 space-y-0.5">
        {points.map((point, i) => (
          <li key={point.attempt_id} className="truncate text-2xs text-ink-muted">
            <span className="tabular text-ink-faint">{i + 1}.</span> {point.exam_title}
            {point.attempt_no > 1 ? (
              <span className="text-ink-faint"> · resit #{point.attempt_no}</span>
            ) : null}
            <span className="tabular text-ink-faint">
              {" "}
              · {formatDate(point.submitted_at)}
            </span>
          </li>
        ))}
      </ol>

      {points.length === 1 ? (
        <p className="mt-1 text-2xs text-ink-faint">
          One graded sitting — there is no trend to read until there is a second.
        </p>
      ) : points.length === 2 ? (
        <p className="mt-1 text-2xs text-ink-faint">
          Two sittings only: this is a difference, not yet a trend.
        </p>
      ) : null}
    </div>
  );
}
